import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@/dataProvider/axios';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { ORG_MEMBER_USAGE_ENDPOINT, ORG_USAGE_ENDPOINT } from '@/services/org-admin';
import type { MemberUsage, OrgUsageRollup } from '@/services/org-admin';

/**
 * The org spend/budget rollup.
 *
 * `refetchInterval` exists for the Billing screen's settling window: a seat top-up's money arrives
 * as that seat's `budget_usd` HERE, not in the billing status or the credit balance — so this is the
 * payload that has to be polled while a top-up settles. Before it was pollable, a top-up polled two
 * payloads that could not carry its outcome and none that could.
 */
export function useOrgUsage(options?: { refetchInterval?: number | false; enabled?: boolean }) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useQuery({
    queryKey: ['org-usage', orgId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<OrgUsageRollup>(ORG_USAGE_ENDPOINT(orgId!));
      return data;
    },
    /*
     * `enabled` exists so a caller that only renders this for SOME readers does not
     * fetch it for the rest. The Usage tab is the case: it shows this rollup to a
     * plain member and the month-scoped report to an admin, but called this
     * unconditionally — so every admin page load fetched a second, differently-clocked
     * spend figure it then never rendered. `/usage` is cycle spend
     * (`live_cycle_spend/2`, max of LiteLLM's counter and our events) while the report
     * is the selected month; on dev 2026-09-11 that was $1.5718689809 against
     * $1.55846944 for the same org at the same moment.
     */
    enabled: !!orgId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval ?? false,
    // Cached data still shows instantly on mount, but every visit to the Usage
    // tab re-fetches regardless of the global 1-minute staleTime — admins land
    // here specifically to check current spend/budget health.
    refetchOnMount: 'always',
  });
}

/**
 * The signed-in member's own spend/budget standing — the self-service counterpart to
 * `useOrgUsage`'s org-wide rollup, for surfaces any member can see (the navbar), not just admins.
 *
 * Hits `GET /orgs/:id/usage/members/:user_id` with the caller's own id, which the backend already
 * authorizes for any member on themselves (`authorize_self_or_admin`) — no new endpoint needed.
 */
export function useMyUsage(options?: { refetchInterval?: number | false }) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const userId = useAuthStoreV1((s) => s.user?.id ?? null);

  return useQuery({
    queryKey: ['my-usage', orgId, userId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<MemberUsage>(
        ORG_MEMBER_USAGE_ENDPOINT(orgId!, userId!),
      );
      return data;
    },
    enabled: !!orgId && !!userId,
    staleTime: 60_000,
    refetchInterval: options?.refetchInterval ?? false,
  });
}
